const RAD_TO_DEGREES = 180 / Math.PI;

const clamp = (value, minimum, maximum) =>
  Math.min(maximum, Math.max(minimum, value));

/**
 * 正脸位置合格范围，所有数值都是相对于摄像头画面宽高的比例。
 *
 * 需要让脸离椭圆更远时，主要修改下面四个值：
 * - 减小 maxWidth / maxHeight：限制脸不能太大，四周留白会更多。
 * - 增大 minCenterX / minCenterY，同时减小 maxCenterX / maxCenterY：
 *   缩小允许的人脸中心区域，避免脸贴近某一侧。
 *
 * 如果现场用户经常因为离镜头太远而不合格，再适当减小 minWidth / minHeight。
 */
export const FACE_POSITION_LIMITS = Object.freeze({
  minCenterX: 0.36, // 人脸中心最左只能到画面宽度的 36%。
  maxCenterX: 0.64, // 人脸中心最右只能到画面宽度的 64%。
  minCenterY: 0.32, // 人脸中心最高只能到画面高度的 32%。
  maxCenterY: 0.68, // 人脸中心最低只能到画面高度的 68%。
  minWidth: 0.12, // 最小人脸宽度；数值越小，允许用户离摄像头越远。
  maxWidth: 0.46, // 最大人脸宽度；数值越小，左右留白越多。
  minHeight: 0.22, // 最小人脸高度；数值越小，允许用户离摄像头越远。
  maxHeight: 0.5, // 最大人脸高度；数值越小，上下留白越多。
});

const getFaceBounds = (landmarks = []) => {
  if (!landmarks.length) return null;

  const xValues = landmarks.map(({ x }) => x);
  const yValues = landmarks.map(({ y }) => y);
  const left = Math.min(...xValues);
  const right = Math.max(...xValues);
  const top = Math.min(...yValues);
  const bottom = Math.max(...yValues);

  return {
    left,
    right,
    top,
    bottom,
    width: right - left,
    height: bottom - top,
    centerX: (left + right) / 2,
    centerY: (top + bottom) / 2,
  };
};

export const getFacePositionValidation = (bounds) => {
  if (!bounds) {
    return { valid: false, failures: ["没有可用的人脸边界数据"] };
  }

  const failures = [];
  if (bounds.centerX < FACE_POSITION_LIMITS.minCenterX) {
    failures.push("人脸中心太靠左");
  }
  if (bounds.centerX > FACE_POSITION_LIMITS.maxCenterX) {
    failures.push("人脸中心太靠右");
  }
  if (bounds.centerY < FACE_POSITION_LIMITS.minCenterY) {
    failures.push("人脸中心太靠上");
  }
  if (bounds.centerY > FACE_POSITION_LIMITS.maxCenterY) {
    failures.push("人脸中心太靠下");
  }
  if (bounds.width < FACE_POSITION_LIMITS.minWidth) {
    failures.push("人脸宽度太小，请靠近摄像头");
  }
  if (bounds.width > FACE_POSITION_LIMITS.maxWidth) {
    failures.push("人脸宽度太大，请远离摄像头");
  }
  if (bounds.height < FACE_POSITION_LIMITS.minHeight) {
    failures.push("人脸高度太小，请靠近摄像头");
  }
  if (bounds.height > FACE_POSITION_LIMITS.maxHeight) {
    failures.push("人脸高度太大，请远离摄像头");
  }

  return { valid: failures.length === 0, failures };
};

export const isFacePositionValid = (bounds) =>
  getFacePositionValidation(bounds).valid;

// MediaPipe 的面部变换矩阵包含头部旋转信息。这里把旋转部分转换成
// 欧拉角；LivenessChallengeMachine 会记录每次检测的正脸基准值，
// 用来消除摄像头安装角度和不同用户脸型造成的小幅偏差。
export const getEulerAngles = (matrix) => {
  const values = matrix?.data;
  if (!values || values.length < 11) return null;

  return {
    pitch: Math.atan2(values[9], values[10]) * RAD_TO_DEGREES,
    yaw: Math.asin(clamp(-values[8], -1, 1)) * RAD_TO_DEGREES,
    roll: Math.atan2(values[4], values[0]) * RAD_TO_DEGREES,
  };
};

const getLandmarkPose = (landmarks, bounds) => {
  const nose = landmarks?.[1];
  const leftTemple = landmarks?.[234];
  const rightTemple = landmarks?.[454];
  const forehead = landmarks?.[10];
  const chin = landmarks?.[152];
  if (!nose || !leftTemple || !rightTemple || !forehead || !chin || !bounds) {
    return null;
  }

  const faceMidX = (leftTemple.x + rightTemple.x) / 2;
  const faceHeight = Math.max(chin.y - forehead.y, 0.001);
  const noseHeightRatio = (nose.y - forehead.y) / faceHeight;

  return {
    yaw: ((nose.x - faceMidX) / Math.max(bounds.width, 0.001)) * 160,
    pitch: (noseHeightRatio - 0.52) * 90,
    roll: 0,
  };
};

export const getFacePose = (result) => {
  const landmarks = result?.faceLandmarks?.[0];
  if (!landmarks?.length) {
    return {
      faceDetected: false,
      positionValid: false,
      bounds: null,
      yaw: 0,
      pitch: 0,
      roll: 0,
    };
  }

  const bounds = getFaceBounds(landmarks);
  const positionValidation = getFacePositionValidation(bounds);
  const pose =
    getEulerAngles(result?.facialTransformationMatrixes?.[0]) ||
    getLandmarkPose(landmarks, bounds);

  return {
    faceDetected: Boolean(pose),
    positionValid: Boolean(pose) && positionValidation.valid,
    positionFailures: positionValidation.failures,
    bounds,
    yaw: pose?.yaw || 0,
    pitch: pose?.pitch || 0,
    roll: pose?.roll || 0,
  };
};
